// src/client/renderer/vfx/tile/golden-chest-arrow.ts
//
// Plan 16, Task 4.4 — Golden chest arrow trail (one-shot path follower).
//
// Visualises a golden arrow that traces from a player's tile to the
// treasure-chest tile. Triggered by EconomyChangeEvent where
// event.resource === 'keys' AND event.newValue >= threshold.
//
// ── Threshold note ────────────────────────────────────────────────────────────
//
// The threshold is supplied by an injected `getChestEligibilityThreshold(): number`
// callback. If the callback is missing, the default threshold is 100.
//
// FOLLOW-UP (Plan 18 / Plan 19): The placeholder threshold value 100 and the
// canonical chest-eligibility threshold (which lives in
// src/shared/game/economy.ts) MUST be reconciled. Until that reconciliation
// lands, the 100 default is intentionally a placeholder — callers in production
// wire-up (Plan 19) MUST supply the real threshold via the callback rather than
// relying on the default.
//
// ── Resolver design ───────────────────────────────────────────────────────────
//
// resolveGoldenChestArrowForEvent() returns args with a placeholder position.
// The path itself is NOT present in EconomyChangeEvent — it is supplied
// externally by the caller via manager.spawn() in the Plan 19 wire-up, exactly
// like path-preview-line (Task 2.3). The resolver returns a non-null result when
// the threshold condition is met, but the path field in args is omitted
// (undefined). pool.acquire() null-guards on path.
//
// ── Single-instance pool ──────────────────────────────────────────────────────
//
// At most ONE active arrow at a time. Calling acquire() while one is active
// REPLACES it — the old emitter is released before the new one is created.
//
// ── Emitter structure ─────────────────────────────────────────────────────────
//
// Each emitter is a THREE.Group containing:
//   - trail: THREE.Line along args.path
//   - arrowHead: THREE.Mesh (ConeGeometry), positioned at path[0] initially,
//     then animated to trace along the path.
//
// ── Animation ─────────────────────────────────────────────────────────────────
//
// Total lifetime: 1.2s.
// Opacity proxy drives both trail + arrowHead opacity:
//   0→1 over 0–200ms (fade-in)
//   1 hold from 200ms–900ms
//   1→0 over 900ms–1200ms (fade-out)
// Arrow head position is interpolated along the path over the full 1.2s.
//
// Completion callback: tl.call(fn, undefined, 1.2) — NOT eventCallback.
// See gsap-registry.ts VIRTUAL_DURATION note.
//
// ── Forward-defense F4 ────────────────────────────────────────────────────────
//
// pool.dispose() cleans any active emitter WITHOUT requiring a prior
// pool.release() call. Idempotent (guarded by `disposed` flag).

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Total lifetime of one emitter in seconds. */
const LIFETIME = 1.2;

/** Fade-in ends at this time (seconds). */
const FADE_IN_END = 0.2;

/** Fade-out starts at this time (seconds). */
const FADE_OUT_START = 0.9;

/** Gold colour matching AMBIENT_GLOW_COLORS.gold. */
const ARROW_COLOR = 0xffd24a;

/** Default threshold for chest eligibility (keys count). Reconcile in Plan 18/19. */
const DEFAULT_CHEST_THRESHOLD = 100;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<GsapRegistry['create']>;
  trailMaterial: THREE.LineBasicMaterial;
  trailGeometry: THREE.BufferGeometry;
  headMaterial: THREE.MeshBasicMaterial;
  headGeometry: THREE.ConeGeometry;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface GoldenChestArrowPoolConfig {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}

export function createGoldenChestArrowPool({
  scene,
  registry,
}: GoldenChestArrowPoolConfig): TileVfxPool {
  // Single-instance: at most one active emitter at a time.
  let activeEmitter: THREE.Object3D | null = null;
  let activeState: EmitterState | null = null;

  let disposed = false;

  // ── internal release helper ────────────────────────────────────────────────

  function releaseActive(): void {
    if (!activeEmitter || !activeState) return;

    activeState.handle.dispose(); // kills gsap timeline, removes from registry
    scene.remove(activeEmitter);

    activeState.trailMaterial.dispose();
    activeState.trailGeometry.dispose();
    activeState.headMaterial.dispose();
    activeState.headGeometry.dispose();

    // Mark finished so any stale reference sees isFinished = true
    activeState.finished = true;

    activeEmitter = null;
    activeState = null;
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Single-instance: release the current emitter before creating a new one.
    if (activeEmitter !== null) {
      releaseActive();
    }

    const path = args.path;

    // Null guard: must have at least one point to draw anything.
    if (!path || path.length === 0) return null;

    // ── Build trail line ───────────────────────────────────────────────────

    const positions = new Float32Array(path.length * 3);
    for (let i = 0; i < path.length; i++) {
      positions[i * 3 + 0] = path[i].x;
      positions[i * 3 + 1] = path[i].y;
      positions[i * 3 + 2] = path[i].z;
    }
    const trailGeometry = new THREE.BufferGeometry();
    trailGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const trailMaterial = new THREE.LineBasicMaterial({
      color: ARROW_COLOR,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const trail = new THREE.Line(trailGeometry, trailMaterial);

    // ── Build arrow head ───────────────────────────────────────────────────

    // Cone: radiusTop=0, radiusBottom=0.07, height=0.18
    // Oriented so tip points along +Y by default. We rotate -90° around Z
    // to point along +X, then let the position proxy handle direction.
    // (For a path follower, the exact orientation is cosmetic for Plan 16;
    // Plan 19 wire-up can refine orientation based on movement direction.)
    const headGeometry = new THREE.ConeGeometry(0.07, 0.18, 8);

    const headMaterial = new THREE.MeshBasicMaterial({
      color: ARROW_COLOR,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const arrowHead = new THREE.Mesh(headGeometry, headMaterial);

    // Start position: first path point
    arrowHead.position.set(path[0].x, path[0].y, path[0].z);

    // ── Group them ────────────────────────────────────────────────────────

    const group = new THREE.Group();
    group.add(trail);
    group.add(arrowHead);
    scene.add(group);

    // ── Build gsap timeline ────────────────────────────────────────────────

    const handle = registry.create('golden_chest_arrow');
    const tl = handle.timeline;

    const state: EmitterState = {
      handle,
      trailMaterial,
      trailGeometry,
      headMaterial,
      headGeometry,
      finished: false,
    };

    // Shared opacity proxy — drives both trail and arrowHead
    const opacityProxy = { value: 0 };

    // Fade-in: 0→1 over first 200ms
    tl.to(
      opacityProxy,
      {
        value: 1,
        duration: FADE_IN_END,
        ease: 'power1.out',
        onUpdate: () => {
          trailMaterial.opacity = opacityProxy.value;
          headMaterial.opacity = opacityProxy.value;
        },
      },
      0,
    );

    // Hold at 1 from 200ms→900ms (no explicit tween needed; the hold gap is
    // covered by the fade-out starting at FADE_OUT_START).

    // Fade-out: 1→0 over last 300ms (900ms–1200ms)
    tl.to(
      opacityProxy,
      {
        value: 0,
        duration: LIFETIME - FADE_OUT_START,
        ease: 'power1.in',
        onUpdate: () => {
          trailMaterial.opacity = opacityProxy.value;
          headMaterial.opacity = opacityProxy.value;
        },
      },
      FADE_OUT_START,
    );

    // Arrow head position: moves along path over the full 1.2s lifetime.
    // We interpolate using a t proxy [0→1] and sample along the path segments.
    const pathVectors = path.map((p) => new THREE.Vector3(p.x, p.y, p.z));
    const tProxy = { value: 0 };

    tl.to(
      tProxy,
      {
        value: 1,
        duration: LIFETIME,
        ease: 'none',
        onUpdate: () => {
          const t = Math.min(Math.max(tProxy.value, 0), 1);
          const worldPos = samplePath(pathVectors, t);
          arrowHead.position.copy(worldPos);
        },
      },
      0,
    );

    // Completion callback — use tl.call(fn, undefined, absolutePos) per spec.
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      LIFETIME,
    );

    activeEmitter = group;
    activeState = state;

    return group;
  }

  // ── update ─────────────────────────────────────────────────────────────────
  // Returns the current active set (size 0 or 1).
  // The registry.update(dt) call is performed by the manager; this pool only
  // returns the active set for iteration.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return activeEmitter ? new Set([activeEmitter]) : new Set<THREE.Object3D>();
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    if (emitter !== activeEmitter) return true; // stale/unknown → treat as finished
    return activeState ? activeState.finished : true;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (emitter !== activeEmitter) return; // safe no-op for stale refs
    releaseActive();
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense F4: cleans up any active emitter without expecting a prior
  // pool.release() call. Idempotent.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    releaseActive();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Path interpolation helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Linearly samples a world position along a multi-segment path.
 *
 * t=0 → path[0], t=1 → path[last].
 * Segments are equally weighted by count (not by length) for simplicity.
 * Plan 19 wire-up can upgrade to arc-length parameterisation if needed.
 */
function samplePath(points: THREE.Vector3[], t: number): THREE.Vector3 {
  if (points.length === 1) return points[0].clone();

  const segments = points.length - 1;
  const scaledT = t * segments;
  const segIndex = Math.min(Math.floor(scaledT), segments - 1);
  const segT = scaledT - segIndex;

  const a = points[segIndex];
  const b = points[segIndex + 1];
  return new THREE.Vector3().lerpVectors(a, b, segT);
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps an EconomyChangeEvent that crosses the key eligibility threshold upward
 * to golden-chest-arrow spawn args.
 *
 * Returns null for:
 *   - any non-economy_change event
 *   - economy_change events where resource !== 'keys'
 *   - economy_change events where keys did not increase
 *   - economy_change events where oldValue was already at/above threshold
 *   - economy_change events where newValue is still below threshold
 *
 * Threshold note:
 *   The threshold is supplied by `getChestEligibilityThreshold()`. When omitted
 *   or undefined, defaults to 100 (placeholder — see leading comment for
 *   Plan 18/19 reconciliation requirement).
 *
 * Path note:
 *   EconomyChangeEvent carries no path. The position placeholder {x:0, y:0, z:0}
 *   is intentional — the wire-up site (Plan 19) resolves tile positions and
 *   supplies path + position via manager.spawn(). pool.acquire() null-guards on
 *   missing path. This is the same pattern as resolvePathPreviewForEvent (Task 2.3).
 */
export function resolveGoldenChestArrowForEvent(
  event: GameEvent,
  getChestEligibilityThreshold?: () => number,
): { poolId: 'golden_chest_arrow'; args: TileVfxSpawnArgs } | null {
  if (event.type !== 'economy_change') return null;
  if (event.resource !== 'keys') return null;

  const threshold = getChestEligibilityThreshold?.() ?? DEFAULT_CHEST_THRESHOLD;
  if (event.newValue <= event.oldValue) return null;
  if (event.oldValue >= threshold) return null;
  if (event.newValue < threshold) return null;

  return {
    poolId: 'golden_chest_arrow',
    args: {
      position: { x: 0, y: 0, z: 0 }, // wire-up (Plan 19) supplies real position + path
    },
  };
}
